/**
 * Full field list for mobile card view on paper-journal row tables.
 */

import React from 'react';
import type { PaperJournalRewardRow } from '@/app/types/paperJournal';
import { DataList } from '@/components/common/DataList';
import { formatPaperJournalPlainInt } from '@/pages/PaperJournal/paperJournalFormat';
import {
  paperJournalEpidLink,
  paperJournalFulfillmentPath,
  paperJournalIdLink,
  paperJournalPartnerLink,
  paperJournalRefUserLink,
  paperJournalRewardPath,
  paperJournalTransactionPath,
  paperJournalUploadPath,
  PaperJournalEntityLink,
} from '@/pages/PaperJournal/paperJournalAdminLinks';
import { formatPaperJournalTimestamp } from '@/pages/PaperJournal/paperJournalTableCells';

import styles from './paperJournalRowDataList.module.scss';

export type PaperJournalDataListItem = {
  label: string;
  value: React.ReactNode;
  copyable?: boolean;
  copyText?: string;
};

export function paperJournalRowToDataListItems(row: PaperJournalRewardRow): PaperJournalDataListItem[] {
  return [
    { label: 'Row id', value: row.id, copyable: true },
    { label: 'CSV row', value: formatPaperJournalPlainInt(row.csv_row_index) },
    { label: 'Status', value: row.status },
    {
      label: 'Ref user id',
      value: paperJournalRefUserLink(row.ref_user_id),
      copyable: true,
      copyText: formatPaperJournalPlainInt(row.ref_user_id),
    },
    {
      label: 'Eligible participant id',
      value: paperJournalEpidLink(row.eligible_participant_id),
      copyable: true,
      copyText: formatPaperJournalPlainInt(row.eligible_participant_id),
    },
    {
      label: 'Partner product id',
      value: paperJournalPartnerLink(row.partner_product_id),
      copyable: true,
      copyText: formatPaperJournalPlainInt(row.partner_product_id),
    },
    { label: 'Reward amount (pts)', value: formatPaperJournalPlainInt(row.reward_amount) },
    { label: 'Failure reason', value: row.failure_reason ?? '—' },
    { label: 'Resolved reward source', value: row.resolved_reward_source ?? '—' },
    {
      label: 'Resolved reward id',
      value: paperJournalIdLink(row.resolved_reward_id, paperJournalRewardPath),
      copyable: row.resolved_reward_id !== null,
      copyText: row.resolved_reward_id ?? undefined,
    },
    {
      label: 'Earn transaction id',
      value: paperJournalIdLink(row.earn_transaction_id, paperJournalTransactionPath),
      copyable: row.earn_transaction_id !== null,
      copyText: row.earn_transaction_id ?? undefined,
    },
    {
      label: 'Redeem transaction id',
      value: paperJournalIdLink(row.redeem_transaction_id, paperJournalTransactionPath),
      copyable: row.redeem_transaction_id !== null,
      copyText: row.redeem_transaction_id ?? undefined,
    },
    {
      label: 'Fulfillment id',
      value: paperJournalIdLink(row.fulfillment_id, paperJournalFulfillmentPath),
      copyable: row.fulfillment_id !== null,
      copyText: row.fulfillment_id ?? undefined,
    },
    { label: 'Uploaded by', value: row.uploaded_by },
    { label: 'Uploaded at', value: formatPaperJournalTimestamp(row.uploaded_at) },
    { label: 'Created at', value: formatPaperJournalTimestamp(row.created_at) },
    { label: 'Updated at', value: formatPaperJournalTimestamp(row.updated_at) },
    { label: 'Original filename', value: row.original_filename },
    {
      label: 'File SHA-256',
      value: (
        <PaperJournalEntityLink to={paperJournalUploadPath(row.file_sha256)} className={styles.fileHash}>
          {row.file_sha256}
        </PaperJournalEntityLink>
      ),
      copyable: true,
      copyText: row.file_sha256,
    },
  ];
}

export function PaperJournalRowMobileCard({
  row,
}: {
  row: PaperJournalRewardRow;
}): React.ReactElement {
  return (
    <DataList
      className={styles.mobileCard}
      items={paperJournalRowToDataListItems(row)}
      columns={1}
    />
  );
}
